/**
 * Sim event -> sound, as one exhaustive table (07 §5). The mapped type makes it a compile error
 * to add a `SimEvent` type without deciding what it sounds like: a one-shot `sound`, a `system`
 * (something continuous or musical that another module drives), or `silent` with a reason.
 * Pure: the engine looks a rule up, asks it for an id, and places the sound where `at` says.
 */

import { isRefusal } from '../sim/damage';
import type { SimEvent } from '../sim/types';
import { BARK_ID, type SfxId } from './sfx';

export type EventType = SimEvent['type'];
export type EventOf<T extends EventType> = Extract<SimEvent, { type: T }>;

/**
 * Where the sound comes from. `event`: the event's own x/z. `enemy` / `captive` / `pod` / `prop`:
 * looked up by the event's id or index. `north`: somewhere off-screen up the map (reinforcements
 * have no position yet). `ui`: not in the world at all - flat, unfiltered, on the UI bus.
 */
export type Placement = 'event' | 'ranger' | 'enemy' | 'captive' | 'pod' | 'prop' | 'north' | 'ui';

/** Modules that also react to an event: `score` (music) and `loops` (siren, Osprey, wind). */
export type AudioSystem = 'score' | 'loops';

export type EventRule<T extends EventType> =
  | {
      kind: 'sound';
      at: Placement;
      /** Every id `pick` can return: what the tests check against the bank. */
      sounds: readonly SfxId[];
      pick: (e: EventOf<T>) => SfxId | null;
      also?: AudioSystem[];
    }
  | { kind: 'system'; systems: AudioSystem[]; why: string }
  | { kind: 'silent'; why: string };

/** One in five rounds that hit something solid sings off it; decided by where it hit, so it is stable. */
function ricochets(x: number, z: number): boolean {
  const h = Math.abs(Math.sin(x * 12.9898 + z * 78.233) * 43758.5453);
  return h - Math.floor(h) < 0.2;
}

export const EVENT_SOUNDS: { [T in EventType]: EventRule<T> } = {
  shot: {
    kind: 'sound', at: 'event', sounds: ['carbine', 'enemyRifle', 'mgInside', 'pistolSuppressed'],
    // The original's most useful cue: fire from inside a fortification has its own timbre (01 §10).
    pick: (e) => (e.suppressed ? 'pistolSuppressed' : e.fromPlayer ? 'carbine' : e.emplacement ? 'mgInside' : 'enemyRifle'),
  },
  impact: {
    kind: 'sound', at: 'event', sounds: ['impactDirt', 'impactHard', 'ricochet'],
    pick: (e) => (e.y <= 0.5 ? 'impactDirt' : ricochets(e.x, e.z) ? 'ricochet' : 'impactHard'),
  },
  knife: {
    kind: 'sound', at: 'event', sounds: ['knifeSwing', 'knifeHit'], pick: (e) => (e.hit ? 'knifeHit' : 'knifeSwing'),
  },
  throw: { kind: 'sound', at: 'event', sounds: ['grenadeThrow'], pick: () => 'grenadeThrow' },
  rocket: { kind: 'sound', at: 'event', sounds: ['lawLaunch'], pick: () => 'lawLaunch' },
  explosion: {
    // The engine swaps in `cookOff` when the blast is a destroyed prop's secondary (same spot, moments later).
    kind: 'sound', at: 'event', sounds: ['explosionSmall', 'explosionLarge', 'cookOff'],
    pick: (e) => (e.big ? 'explosionLarge' : 'explosionSmall'),
  },
  propDestroyed: { kind: 'sound', at: 'prop', sounds: ['debris'], pick: () => 'debris' },
  enemyHit: {
    // ROUND8: the engine swaps in a yelp when the one hit is a dog (dogVoice.ts): a man's cry would be wrong.
    kind: 'sound', at: 'enemy', sounds: ['enemyHit', 'enemyKilled', 'dogYelp'],
    pick: (e) => (e.killed ? 'enemyKilled' : 'enemyHit'),
  },
  rangerHit: {
    kind: 'sound', at: 'ranger', sounds: ['rangerHit', 'rangerWounded'],
    pick: (e) => (e.wounded ? 'rangerWounded' : 'rangerHit'),
  },
  rangerDowned: { kind: 'sound', at: 'ranger', sounds: ['rangerDowned'], pick: () => 'rangerDowned' },
  alert: {
    kind: 'sound', at: 'ui', sounds: ['alert1', 'alert2', 'alert3'], also: ['score'],
    pick: (e) => (e.level >= 3 ? 'alert3' : e.level === 2 ? 'alert2' : e.level === 1 ? 'alert1' : null),
  },
  spotted: { kind: 'sound', at: 'ui', sounds: ['spotted'], pick: () => 'spotted' },
  pickup: {
    // A refusal is worded as a pickup so it reaches the banner, but it must not SOUND like a reward: the
    // sim owns the test (`isRefusal`), so the words and the cue can never drift apart.
    kind: 'sound', at: 'ranger', sounds: ['pickup', 'heal', 'refused'],
    pick: (e) => (isRefusal(e.what) ? 'refused' : e.what.includes('WOUND') ? 'heal' : 'pickup'),
  },
  dryFire: { kind: 'sound', at: 'ranger', sounds: ['dryFire'], pick: () => 'dryFire' },
  reload: { kind: 'sound', at: 'ranger', sounds: ['reload'], pick: () => 'reload' },
  bombPlaced: { kind: 'sound', at: 'event', sounds: ['bombPlaced'], also: ['loops'], pick: () => 'bombPlaced' },
  objective: { kind: 'sound', at: 'ui', sounds: ['objectiveProgress'], pick: () => 'objectiveProgress' },
  osprey: {
    // The aircraft itself is a continuous voice driven from `world.osprey`; the event adds the radio call.
    kind: 'sound', at: 'ui', sounds: ['radio'], also: ['loops', 'score'],
    pick: (e) => (e.phase === 'inbound' ? 'radio' : null),
  },
  reinforcements: { kind: 'sound', at: 'north', sounds: ['whistle'], pick: () => 'whistle' },
  // ROUND8: a dog at bay barks in pairs - the engine swaps in the double from the dog's mode (dogVoice.ts).
  bark: { kind: 'sound', at: 'event', sounds: [...Object.values(BARK_ID), 'dogBay'], pick: (e) => BARK_ID[e.kind] },
  siren: { kind: 'system', systems: ['loops'], why: 'a loop that wails while alert stays 3, for up to 20 s' },
  objectiveFailed: { kind: 'sound', at: 'ui', sounds: ['objectiveFailed'], pick: () => 'objectiveFailed' },
  objectiveComplete: {
    kind: 'sound', at: 'ui', sounds: ['objectiveComplete'], also: ['score'], pick: () => 'objectiveComplete',
  },
  captive: {
    kind: 'sound', at: 'captive', sounds: ['captiveFreed', 'objectiveProgress', 'captiveLost', 'surrender'],
    pick: (e) => (e.what === 'freed' ? 'captiveFreed' : e.what === 'boarded' ? 'objectiveProgress'
      : e.what === 'lost' ? 'captiveLost' : 'surrender'),
  },
  disguise: { kind: 'sound', at: 'ranger', sounds: ['cloth'], pick: () => 'cloth' },
  mine: { kind: 'sound', at: 'event', sounds: ['mine'], pick: () => 'mine' },
  iceBreak: { kind: 'sound', at: 'event', sounds: ['iceBreak'], pick: () => 'iceBreak' },
  signal: { kind: 'sound', at: 'ui', sounds: ['signalBeep'], pick: () => 'signalBeep' },
  podDropped: { kind: 'sound', at: 'ui', sounds: ['podDropped'], pick: () => 'podDropped' },
  podLanded: {
    kind: 'sound', at: 'pod', sounds: ['podLanded', 'podSmashed'], pick: (e) => (e.intact ? 'podLanded' : 'podSmashed'),
  },
  podOpened: { kind: 'sound', at: 'pod', sounds: ['podOpened'], pick: () => 'podOpened' },
  propRemoved: { kind: 'sound', at: 'ui', sounds: ['jetsAway'], pick: () => 'jetsAway' },
  smoke: { kind: 'sound', at: 'event', sounds: ['smokeHiss'], pick: () => 'smokeHiss' },
  tagsRecovered: { kind: 'sound', at: 'ui', sounds: ['tagsTaken'], pick: () => 'tagsTaken' },
  // ROUND3 PLACEHOLDER: the expansion's timed events and blackouts borrow existing cues until they have their own
  // (a train whistle, engines starting, a generator winding down). A window closing is said by the HUD alone.
  timedEvent: {
    kind: 'sound', at: 'ui', sounds: ['radio', 'signalBeep', 'whistle'],
    pick: (e) => (e.phase === 'windowClosed' ? null : e.phase === 'hastened' ? 'signalBeep'
      : e.effect === 'searchPartyArrives' || e.effect === 'convoyCrosses' ? 'whistle' : 'radio'),
  },
  blackout: { kind: 'sound', at: 'ui', sounds: ['debris'], pick: () => 'debris' },
  // ROUND4 PLACEHOLDER: a vehicle borrows the jets' departing roar as it drives off the map and the debris of
  // a destroyed prop for its wreck, until engines of their own exist. Men getting down are said by the HUD.
  vehicle: {
    kind: 'sound', at: 'event', sounds: ['jetsAway', 'debris'],
    pick: (e) => (e.what === 'left' ? 'jetsAway' : e.what === 'wrecked' ? 'debris' : null),
  },
  jump: { kind: 'sound', at: 'ui', sounds: ['chuteOpen'], also: ['loops', 'score'], pick: () => 'chuteOpen' },
  landed: {
    kind: 'sound', at: 'ui', sounds: ['landThump', 'splash', 'wireSnag'], also: ['score'],
    pick: (e) => (e.hazard === 'water' ? 'splash' : e.hazard === 'wire' ? 'wireSnag' : 'landThump'),
  },
  ended: {
    kind: 'system', systems: ['score'], why: 'the debrief cue is music: success sting, loss cue or the memorial motif',
  },
};

/** Event types that make no sound at all, on purpose. Empty today; the mechanism is here for the day one is added. */
export const SILENT_EVENTS: readonly EventType[] = (Object.keys(EVENT_SOUNDS) as EventType[])
  .filter((type) => EVENT_SOUNDS[type].kind === 'silent');

interface LooseRule {
  kind: 'sound' | 'system' | 'silent';
  at?: Placement;
  pick?: (e: SimEvent) => SfxId | null;
}

/** The one-shot for an event, or null. (The cast bridges TypeScript's correlated-union gap.) */
export function soundIdFor(e: SimEvent): SfxId | null {
  const rule = EVENT_SOUNDS[e.type] as LooseRule;
  return rule.kind === 'sound' && rule.pick ? rule.pick(e) : null;
}

/** Where an event type's one-shot is placed; `ui` for the types that have none. */
export function placementFor(type: EventType): Placement {
  return (EVENT_SOUNDS[type] as LooseRule).at ?? 'ui';
}
